import React, { useState } from "react";

// Components
import Button from "../../atoms/button/button";
import Modal from "../../ui/modal/Modal";
import FormInputAceleradora from "../../atoms/form-input-aceleradora/FormInputAceleradora";
import Loading from "../../atoms/loading/loading";

// Utils
import rawToReact from "../../../utils/rawToReact";
import useLockScroll from "../../../hooks/useBlockScroll";

// Services
import {
    getToken,
    getUserFormDataByQuestionId,
} from "../../../services/formServices";
import { useFormPreloadCeidDataContent } from "./query";

// Interfaces
import { PreRegisterCEIDData } from "../../../interfaces/FormCEID";

// Styles
import "./ModalPreloadCeidData.scss";

type FormInputAceleradoraProps = React.ComponentProps<
    typeof FormInputAceleradora
>;

interface ModalPreloadCeidDataProps {
    showModal: boolean;
    onClose: VoidFunction;
    onSuccesPreloadData?: (
        data: NonNullable<PreRegisterCEIDData["answerData"]>
    ) => void;
}

const MAIN_CLASS = "m-modal-preload-ceid-data";

const PRECEID_FORM_ID = 11;

const PRECEID_FORM_DOCUMENT_QUESTION_ID = "docNumber";

const PRECEID_LINK =
    "https://www.comfama.com/servicio-de-empleo/personas/preregistro-centro-empleo-digital/";

const ModalPreloadCeidData = ({
    showModal,
    onClose,
    onSuccesPreloadData,
}: ModalPreloadCeidDataProps) => {
    const content = useFormPreloadCeidDataContent();

    const [customErrorMessageList, setCustomErrorMessageList] = useState<
        {
            nombreId: string;
            errorMessage: string;
        }[]
    >([]);

    const [inputsErrorList, setInputErrorList] = useState<string[]>([]);

    const [formValues, setFormValues] = useState<Record<string, string>>({});

    const [isLoading, setIsLoading] = useState(false);

    const [validationFailed, setValidationFailed] = useState(false);

    useLockScroll(showModal);

    const disableButton = content?.referenciaFormulario?.some((input) => {
        const isRequired = input.obligatorio;
        const hasValue = formValues[input.nombreId];

        if (isRequired && !hasValue) {
            return true;
        }

        return false;
    });

    const handleValueChange = (nombreId: string, value: string) => {
        setFormValues((prev: any) => {
            return { ...prev, [nombreId]: value };
        });
    };

    const inputValidation = (
        type: FormInputAceleradoraProps["type"],
        value,
        nombreId
    ) => {
        switch (type) {
            case "number":
                // validate document number, min 7 caracters
                if (value?.length < 5 && value) {
                    setCustomErrorMessageList((prev: any) => [
                        ...prev,
                        {
                            nombreId,
                            errorMessage: "Por favor ingrese un número válido",
                        },
                    ]);
                    return true;
                } else {
                    return false;
                }
            default:
                return false;
        }
    };

    const validateRequired = () => {
        setCustomErrorMessageList([]);

        const inputs = content?.referenciaFormulario;

        // Agrega los campos no diligenciados a la lista de error
        const errorList: string[] = [];

        inputs.map((input) => {
            if (input.obligatorio && !formValues[input.nombreId]) {
                errorList.push(input.nombreId);
            }

            if (
                inputValidation(
                    input.tipoCampo as FormInputAceleradoraProps["type"],
                    formValues[input.nombreId],
                    input.nombreId
                )
            ) {
                errorList.push(input.nombreId);
            }
        });

        //Controla el scroll y lo lleva hasta el campo con error, que no este diligenciado.
        if (errorList.length > 0) {
            document.getElementById(errorList[0])?.scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: "center",
            });
        }
        setInputErrorList(errorList);
        return errorList;
    };

    const handleSubmit = () => {
        const errors = validateRequired();
        if (!errors?.length) {
            validatePreRegisterInCeid();
        }
    };

    const validatePreRegisterInCeid = async () => {
        setIsLoading(true);
        try {
            if (!formValues.docNumber) {
                throw new Error("Se requiere el número de documento");
            }

            const token = await getToken();
            const response = await getUserFormDataByQuestionId(
                token,
                PRECEID_FORM_ID,
                PRECEID_FORM_DOCUMENT_QUESTION_ID,
                formValues.docNumber
            );

            const data: PreRegisterCEIDData = response.data;

            if (!data.answerData || !data.creationDate)
                throw new Error("No se encontraron datos del usuario");

            const registrationWasThisYear =
                new Date(data.creationDate).getFullYear() ===
                new Date().getFullYear();

            if (!registrationWasThisYear)
                throw new Error("El preregistro no fue realizado este año");

            const isSameTypeOfDocument =
                data.answerData?.document_type?.toLowerCase()?.trim() ===
                formValues?.document_type?.toLowerCase()?.trim();

            if (!isSameTypeOfDocument)
                throw new Error("El tipo de documento no coincide");

            onSuccesPreloadData?.(data.answerData);
        } catch (error) {
            console.error(error);
            setValidationFailed(true);
        } finally {
            setIsLoading(false);
        }
    };

    if(!showModal) return null

    return (
        <div className={MAIN_CLASS}>
            <Modal showModal={showModal} onClose={onClose}>
                <div
                    className={`${MAIN_CLASS}__container ${
                        validationFailed ? "error" : ""
                    } `}
                >
                    <h3 className={`${MAIN_CLASS}__title`}>
                        {validationFailed
                            ? content?.tituloSeccion
                            : content?.tituloGeneral}
                    </h3>

                    <p
                        className={`${MAIN_CLASS}__description ${
                            validationFailed ? "error" : ""
                        }`}
                    >
                        {rawToReact(
                            validationFailed
                                ? content?.descripcionFija?.raw
                                : content?.descripcion?.raw,
                            {}
                        )}
                    </p>

                    <div className={`${MAIN_CLASS}__form`}>
                        {!validationFailed ? (
                            <section className={`${MAIN_CLASS}__form__inputs`}>
                                {content?.referenciaFormulario?.map((input) => {
                                    const hasError = inputsErrorList.includes(
                                        input.nombreId
                                    );

                                    const customError =
                                        customErrorMessageList.find(
                                            (e) => e.nombreId === input.nombreId
                                        )?.errorMessage;

                                    return (
                                        <FormInputAceleradora
                                            disabled={isLoading}
                                            key={input.id}
                                            value={formValues[input.nombreId]}
                                            setInputValue={handleValueChange}
                                            title={input.inputPrincipal}
                                            nombreId={input.nombreId}
                                            options={input.listaSelect}
                                            placeholder={input.placeholder}
                                            isRequired={input.obligatorio}
                                            textoTerminos={undefined}
                                            type={
                                                input.tipoCampo as FormInputAceleradoraProps["type"]
                                            }
                                            isError={hasError}
                                            errorMessage={
                                                ((customError || hasError) &&
                                                    customError) ||
                                                input.mensajeError
                                            }
                                        />
                                    );
                                })}
                            </section>
                        ) : null}

                        <div className={`${MAIN_CLASS}__form__buttons`}>
                            {isLoading ? (
                                <Loading />
                            ) : (
                                <Button
                                    type={validationFailed ? "link" : "button"}
                                    href={
                                        validationFailed
                                            ? PRECEID_LINK
                                            : undefined
                                    }
                                    text={
                                        validationFailed
                                            ? content?.redireccionBoton
                                            : content.tituloBoton
                                    }
                                    classname={`a-button__magenta big center ${validationFailed ? "error" : ""} `}
                                    isDisabled={disableButton || isLoading}
                                    onClickAction={handleSubmit}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ModalPreloadCeidData;
